import styled from 'styled-components'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import { FieldValidator } from 'final-form'
import { useNavigate } from 'react-router-dom'

import { Flex } from '../Flex'
import { themeColor } from '../theme'
import { Box } from '../Box'
import ReportSectionHeader from '../ReportSectionHeader'
import { useBriqTheme } from '../../hooks/useBriqTheme'
import { Text } from '../Text'
import { Space } from '../Space'
import { createTFunction, getCompanyCountry } from '../../utils/helpers'
import { createFormValidator, error, required } from '../../utils/validation'
import { SelectField } from '../../containers/FormFields/SelectField'
import { Button } from '../Button'
import { Icon } from '../Icons'
import { CompanyInfoBoxes } from '../CompanyInfoBoxes/CompanyInfoBoxes'
import {
  AddToCartRequestBody,
  OrderActions,
  getNumberOfOrderItems,
  getOrderId,
  isAddingToCartOrBuyingNow,
  isReportAddingToCart,
  isReportBuyingNow
} from '../../redux/order'
import { ReportType } from '../../redux/search'
import { RootState } from '../../redux/types'
import { DomainType, getDomain, getDomainsLoading } from '../../redux/domains'
import { DialogActions } from '../../redux/dialog'
import { PriceWrapper } from '../../containers/PriceWrapper'
import { FieldSpace } from '../FieldSpace'
import { useViewportSize } from '../../hooks'

import { SearchResultItemProps } from './types'
import { SearchResultItemHeader } from './SearchResultItemHeader'

const StyledSearchResultItem = styled(Flex)`
  border: 1px solid ${themeColor('borderPrimary')};
  border-radius: 4px;
  width: 100%;
`

const StyledFlex = styled(Flex)`
  flex-wrap: wrap;
  background-color: ${themeColor('bgPrimary')};
  border-radius: 4px;
`

type FormValues = {
  mode: 'BuyNow' | 'AddToCart'
  deliveryOption?: string
  language?: string
  acceptedConsent?: string
}

const requiredWhenReportTypeOffline = (): FieldValidator<any> => (value, values: any, meta) => {
  if (meta?.data?.reportType === ReportType.Online) return undefined
  return _.isNil(value) ? error('Required') : undefined
}

const validator = createFormValidator<FormValues>({
  deliveryOption: [requiredWhenReportTypeOffline()],
  language: [required()]
})

export const SearchResultItemFieldValue = (
  { result, umbracoProductData, index, generateFields, renderDeliveryOptionLabel, consentData }: SearchResultItemProps,
  country: string
) => {
  const { t, i18n } = useTranslation(['core', 'search', 'form'])
  const tc = createTFunction('search:', t)
  const tf = createTFunction('form:', t)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const viewPort = useViewportSize()
  const isMobile = viewPort?.isMobile ?? false

  const orderId = useSelector(getOrderId)
  const cartItemsCount = useSelector(getNumberOfOrderItems)
  const countryCode = getCompanyCountry(result.companyIdentifier)

  const cartItemSubmitting = useSelector(isAddingToCartOrBuyingNow)

  const fields = _.filter(generateFields ? generateFields(result, country) : [], x => !_.isEmpty(x.value))
  const theme = useBriqTheme()

  const preferredReport = _.head(result.reports)
  const umbracoPreferredReport = _.find(umbracoProductData, upr => upr.bifCode === preferredReport?.code)

  const isAddingToCart = useSelector((state: RootState) =>
    isReportAddingToCart(state, { companyId: result?.id || '', reportCode: preferredReport?.code || '' })
  )
  const isBuyingNow = useSelector((state: RootState) =>
    isReportBuyingNow(state, { companyId: result?.id || '', reportCode: preferredReport?.code || '' })
  )

  const languages = useSelector((state: RootState) =>
    getDomain(state, { domain: DomainType.Culture, language: i18n.language })
  )
  const deliveryOptions = useSelector((state: RootState) =>
    getDomain(state, { domain: DomainType.DeliveryOption, language: i18n.language })
  )
  const domainsLoading = useSelector(getDomainsLoading)

  const filteredLanguages = _.filter(languages, x => _.includes(preferredReport?.languages, x.code))
  const defaultReportLanguage = _.size(filteredLanguages) === 1 ? filteredLanguages[0].code : undefined
  const filteredDeliveryOptions = _.filter(
    deliveryOptions,
    x => !!_.find(preferredReport?.deliveryOptions, y => x.code === y.code)
  )
  const defaultDeliveryOption = _.size(deliveryOptions) === 1 ? _.head(deliveryOptions)?.code : undefined
  const displayDeliveryOption = preferredReport?.type === ReportType.Offline

  return (
    <StyledSearchResultItem flexDirection="column">
      <SearchResultItemHeader
        hasBorderBottom={!_.isEmpty(fields)}
        companyName={result.name || ''}
        companyIdentifier={result.companyIdentifier}
        companyIdentifierType={result.companyIdentifierType}
      />

      {!_.isEmpty(fields) && (
        <Box pl={3} pr={3} pt={[2, 20]} pb={[2, 20]}>
          <StyledFlex pl={[2, 2, 3]} pt={[2, 2, 3]} pr={[2, 2, 3]}>
            <CompanyInfoBoxes fields={fields} defaultDesktopWidth={0.25} pb={2} />
          </StyledFlex>
        </Box>
      )}

      {preferredReport && (
        <Form<FormValues>
          initialValues={{ deliveryOption: defaultDeliveryOption, language: defaultReportLanguage }}
          validate={validator({ reportType: preferredReport.type })}
          onSubmit={values => {
            const deliveryOptionCode = preferredReport.type === ReportType.Offline ? values.deliveryOption : undefined

            const deliveryOption = _.find(preferredReport.deliveryOptions, x => x.code == deliveryOptionCode)

            const price = deliveryOption?.grossValue
            const currency = deliveryOption?.currency

            const formData: AddToCartRequestBody = {
              orderId,
              customerRequests: [
                {
                  lastUpdateDate: preferredReport.lastUpdateDate,
                  entityName: result.name || '',
                  entityIdentifierEncoded: result.id,
                  countryCode,
                  deliveryOptionCode,
                  languageCode: values.language || '',
                  productCode: preferredReport.code || '',
                  consentProvided: values.acceptedConsent === preferredReport.code,
                  materialTypePriceParam: preferredReport.materialTypePriceParam,
                  countryCodePriceParam: preferredReport.countryCodePriceParam,
                  isFinancialPriceParam: preferredReport.isFinancialPriceParam,
                  inRevisionPriceParam: preferredReport.inRevisionPriceParam,
                  dataTypePriceParam: preferredReport.dataTypePriceParam
                }
              ]
            }

            if (preferredReport.consentRequired && values.acceptedConsent !== preferredReport.code) {
              if (
                !_.isEmpty(consentData?.okButtonText) &&
                !_.isEmpty(consentData?.content) &&
                !_.isEmpty(consentData?.title)
              ) {
                dispatch(
                  DialogActions.showCompanyConsentDialog({
                    productCode: preferredReport.code || '',
                    content: consentData?.content || '',
                    okButtonText: consentData?.okButtonText || '',
                    title: consentData?.title || '',
                    cancelButtonText: consentData?.cancelButtonText
                  })
                )
                return undefined
              }
            }

            if (values.mode === 'AddToCart') {
              dispatch(
                OrderActions.addToCart({
                  companyId: result.id,
                  reportCode: preferredReport.code ?? '',
                  formData,
                  gtmData: {
                    button: index * 2 - 1,
                    cartItemsCount,
                    company: result,
                    currency,
                    value: price,
                    price,
                    reportName: preferredReport.orderingName
                  },
                  navigate
                })
              )
            } else if (values.mode === 'BuyNow') {
              dispatch(
                OrderActions.buyNow({
                  companyId: result.id,
                  reportCode: preferredReport.code ?? '',
                  formData,
                  gtmData: {
                    button: index * 2,
                    cartItemsCount,
                    company: result,
                    currency,
                    value: price,
                    price,
                    reportName: preferredReport.orderingName
                  },
                  navigate
                })
              )
            }
          }}
          render={({ form, handleSubmit, values }) => {
            const priceOption =
              preferredReport?.type === ReportType.Offline
                ? values.deliveryOption
                  ? _.find(preferredReport?.deliveryOptions, x => x.code === values.deliveryOption)
                  : undefined
                : _.head(preferredReport.deliveryOptions)

            return (
              <form onSubmit={handleSubmit}>
                <Space direction="vertical" flexDirection="column" m={3} space={2}>
                  <Box backgroundColor={theme.colors.primary} p={1}>
                    <ReportSectionHeader>{tc('AvailableProduct')}</ReportSectionHeader>
                  </Box>
                  <Space
                    ml={2}
                    mr={2}
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    flexDirection={isMobile ? 'column' : 'row'}
                    space={isMobile ? 1 : 5}
                    flexWrap={isMobile ? 'nowrap' : 'wrap'}
                    alignItems={isMobile ? 'flex-start' : 'center'}
                  >
                    <Text m={1} bold color="primary" noWrap minWidth="auto">
                      {umbracoPreferredReport?.name ?? preferredReport?.bifName}
                    </Text>
                    {displayDeliveryOption && (
                      <FieldSpace m={1} minWidth={isMobile ? '100%' : '300px'} alignItems="center">
                        <Text block bold color="primary" noWrap minWidth="auto">
                          {tf('DeliveryOption')}:
                        </Text>
                        <SelectField
                          name="deliveryOption"
                          placeholder={tf('DeliveryOptionPlaceholder')}
                          options={_.map(filteredDeliveryOptions, x => {
                            const d = _.find(preferredReport.deliveryOptions, y => y.code === x.code)

                            return {
                              label: renderDeliveryOptionLabel(x, d, t),
                              value: x.code
                            }
                          })}
                          additionalProps={{
                            $colorVariant: 'primary',
                            loading: domainsLoading[DomainType.DeliveryOption],
                            className: 'primaryColorVariantSelect'
                          }}
                        />
                      </FieldSpace>
                    )}
                    <FieldSpace m={1} minWidth={isMobile ? '100%' : '300px'} alignItems="center">
                      <Text block bold color="primary" display="flex" alignItems="center" noWrap minWidth="auto">
                        {tf('Language')}:
                      </Text>
                      <SelectField
                        name="language"
                        placeholder={tf('LanguagePlaceholder')}
                        options={_.map(filteredLanguages, x => ({
                          label: x.description,
                          value: x.code
                        }))}
                        additionalProps={{
                          $colorVariant: 'primary',
                          loading: domainsLoading[DomainType.Culture],
                          className: 'primaryColorVariantSelect'
                        }}
                      />
                    </FieldSpace>
                    {priceOption && (
                      <Box m={1}>
                        <PriceWrapper
                          grossValue={priceOption.displayGrossValue}
                          netValue={priceOption.displayNetValue}
                          showPriceForAnonymousUser={false}
                        />
                      </Box>
                    )}
                    <Space m={1} space={1}>
                      <Button
                        variant="primary"
                        loading={isAddingToCart}
                        disabled={cartItemSubmitting}
                        onClick={() => {
                          form.change('mode', 'AddToCart')
                        }}
                        htmlType="submit"
                      >
                        <Icon type="ShoppingCartOutlined" color="white" />
                      </Button>
                      <Button
                        className="qa--buy-now-button"
                        variant="secondary"
                        htmlType="submit"
                        loading={isBuyingNow}
                        disabled={cartItemSubmitting}
                        onClick={() => {
                          form.change('mode', 'BuyNow')
                        }}
                      >
                        {t('BuyNow')}
                      </Button>
                    </Space>
                  </Space>
                </Space>
              </form>
            )
          }}
        ></Form>
      )}
    </StyledSearchResultItem>
  )
}
